import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Award,
  BookOpen,
  Clock,
  GraduationCap,
  Loader2,
  Star,
  Target,
  Users,
} from 'lucide-react';
import EnrollmentModal from '../../components/enrollment/EnrollmentModal';
import { allCourses, courseDetails } from '../../data/courses';
import { getCourseWithContent } from '../../lib/lms';

interface MarketingCourse {
  id: string;
  title: string;
  category: string;
  description: string;
  tagline: string;
  overview: string;
  duration: string;
  students: string;
  level: string;
  rating: number;
  price: string;
  priceValue: number;
  image: string;
  modules: { title: string; topics: string[] }[];
  outcomes: string[];
  certification: string;
  format: string;
  startDate: string;
}

const fallbackImage = allCourses[0]?.image || '';

const priceValueFromLabel = (label: string) => Number(label.replace(/[^\d]/g, '')) || 0;

const formatPrice = (value: number) =>
  value === 0 ? 'Free' : `₦${value.toLocaleString('en-NG')}`;

const CourseDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const [course, setCourse] = useState<MarketingCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    let cancelled = false;
    setLoading(true);

    void getCourseWithContent(id)
      .then((dbCourse) => {
        if (cancelled) return;

        const base = allCourses.find((item) => item.id === id);
        const details = courseDetails[id];

        if (!base && !dbCourse) {
          setCourse(null);
          setLoading(false);
          return;
        }

        const priceValue =
          dbCourse?.price_value ?? details?.priceValue ?? priceValueFromLabel(base?.price || '');

        const modules =
          details?.modules ||
          (dbCourse?.modules ?? []).map((module) => ({
            title: module.title,
            topics: module.lessons.map((lesson) => lesson.title),
          }));

        setCourse({
          id,
          title: dbCourse?.title || base?.title || 'Program',
          category: dbCourse?.category || base?.category || 'Program',
          description: base?.description || dbCourse?.tagline || dbCourse?.description || '',
          tagline: dbCourse?.tagline || details?.tagline || '',
          overview: details?.overview || dbCourse?.description || base?.description || '',
          duration: dbCourse?.duration || base?.duration || 'Self-paced',
          students: base?.students || 'New cohort',
          level: dbCourse?.level || base?.level || 'All levels',
          rating: base?.rating ?? 5,
          price: formatPrice(priceValue),
          priceValue,
          image: dbCourse?.thumbnail || base?.image || fallbackImage,
          modules,
          outcomes: details?.outcomes || [],
          certification: details?.certification || `LSEM ${dbCourse?.category || base?.category || 'Program'} certificate`,
          format: details?.format || '100% remote',
          startDate: details?.startDate || 'Rolling admissions',
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setCourse(null);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 pt-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Course not found</h1>
            <Link
              to="/courses"
              className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-700"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Courses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const enrollLabel =
    course.priceValue === 0 ? 'Enroll Free' : `Enroll Now for ${course.price}`;

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="bg-blue-600">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div>
              <Link
                to="/courses"
                className="mb-6 inline-flex items-center text-blue-100 hover:text-white"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Courses
              </Link>
              <h1 className="mb-4 text-4xl font-bold text-white">{course.title}</h1>
              <p className="mb-8 text-xl text-blue-100">{course.tagline || course.description}</p>
              <div className="mb-8 flex flex-wrap gap-4">
                <div className="flex items-center rounded-full bg-blue-500/30 px-4 py-2 text-white">
                  <Clock className="mr-2 h-5 w-5" />
                  {course.duration}
                </div>
                <div className="flex items-center rounded-full bg-blue-500/30 px-4 py-2 text-white">
                  <Users className="mr-2 h-5 w-5" />
                  {course.students}
                </div>
                <div className="flex items-center rounded-full bg-blue-500/30 px-4 py-2 text-white">
                  <Star className="mr-2 h-5 w-5" />
                  {course.rating} rating
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center rounded-md border border-transparent bg-white px-6 py-3 text-base font-medium text-blue-600 hover:bg-blue-50"
              >
                {enrollLabel}
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
            <div className="relative">
              <div className="overflow-hidden rounded-xl bg-gradient-to-br from-blue-400 to-sky-300 shadow-2xl">
                {course.image && (
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                )}
              </div>
              <div className="absolute -bottom-6 -right-6 rounded-xl bg-white p-4 shadow-xl">
                <GraduationCap className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-8 rounded-xl bg-white p-8 shadow-lg">
              <div className="mb-6 flex items-center">
                <BookOpen className="mr-2 h-6 w-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">Course Syllabus</h2>
              </div>
              {course.modules.length > 0 ? (
                <div className="space-y-5">
                  {course.modules.map((module, index) => (
                    <div key={`${module.title}-${index}`} className="flex items-start">
                      <div className="mr-3 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{module.title}</p>
                        {module.topics.length > 0 && (
                          <ul className="mt-2 space-y-1.5">
                            {module.topics.map((topic) => (
                              <li key={topic} className="flex items-start text-sm text-gray-600">
                                <span className="mr-2 mt-2 h-1 w-1 flex-none rounded-full bg-blue-600" />
                                {topic}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-600">
                  The curriculum for this program will be published shortly. Speak to admissions for the
                  detailed outline.
                </p>
              )}
            </div>

            {course.outcomes.length > 0 && (
              <div className="rounded-xl bg-white p-8 shadow-lg">
                <div className="mb-6 flex items-center">
                  <Target className="mr-2 h-6 w-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Learning Outcomes</h2>
                </div>
                <div className="grid gap-4">
                  {course.outcomes.map((outcome) => (
                    <div key={outcome} className="flex items-start">
                      <Award className="mr-3 h-5 w-5 flex-shrink-0 text-blue-600" />
                      <p className="text-gray-600">{outcome}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-8">
            <div className="rounded-xl bg-white p-6 shadow-lg">
              <h3 className="mb-4 text-xl font-bold text-gray-900">Course Information</h3>
              <div className="space-y-4">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Duration</span>
                  <span className="text-right font-medium">{course.duration}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Level</span>
                  <span className="text-right font-medium">{course.level}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Students</span>
                  <span className="text-right font-medium">{course.students}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Rating</span>
                  <span className="flex items-center font-medium">
                    <Star className="mr-1 h-5 w-5 fill-current text-yellow-400" />
                    {course.rating}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Format</span>
                  <span className="text-right font-medium">{course.format}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-600">Starts</span>
                  <span className="text-right font-medium">{course.startDate}</span>
                </div>
                <div className="flex justify-between gap-4 border-t border-slate-100 pt-4">
                  <span className="text-gray-600">Price</span>
                  <span className="font-bold text-blue-600">{course.price}</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-blue-50 p-6">
              <h3 className="mb-4 text-xl font-bold text-gray-900">Need Help?</h3>
              <p className="mb-4 text-gray-600">
                Have questions about this course? Contact our academic advisors for guidance.
              </p>
              <Link to="/contact" className="inline-flex items-center text-blue-600 hover:text-blue-700">
                Contact Support
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <EnrollmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        courseId={course.id}
        courseTitle={course.title}
        coursePrice={course.price}
        priceValue={course.priceValue}
      />
    </div>
  );
};

export default CourseDetailPage;
